import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MATERIAL_PATHS, MATERIAL_VIEWBOX } from './material.generated';
/**
 * The shared frame. Fill is `currentColor` and there is no stroke, fixed HERE
 * rather than per call: a set that lets a caller add a stroke is a set that
 * ends up two weights.
 */
function Icon({ size = 22, title, viewBox, children, ...rest }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: viewBox, fill: "currentColor", "aria-hidden": title ? undefined : true, role: title ? 'img' : undefined, focusable: "false", ...rest, children: [title ? _jsx("title", { children: title }) : null, children] }));
}
/** One Material glyph, looked up by the name the import script assigned it. */
function Material({ glyph, ...props }) {
    return (_jsx(Icon, { viewBox: MATERIAL_VIEWBOX, ...props, children: _jsx("path", { d: MATERIAL_PATHS[glyph] }) }));
}
export function IconHome(props) {
    return _jsx(Material, { glyph: "home", ...props });
}
export function IconCompass(props) {
    return _jsx(Material, { glyph: "compass", ...props });
}
export function IconBoat(props) {
    return _jsx(Material, { glyph: "boat", ...props });
}
export function IconBook(props) {
    return _jsx(Material, { glyph: "book", ...props });
}
export function IconCommunity(props) {
    return _jsx(Material, { glyph: "community", ...props });
}
export function IconRobot(props) {
    return _jsx(Material, { glyph: "robot", ...props });
}
export function IconCrew(props) {
    return _jsx(Material, { glyph: "crew", ...props });
}
export function IconBell(props) {
    return _jsx(Material, { glyph: "bell", ...props });
}
/**
 * The megaphone — OFFICIAL announcements, and never the bell.
 *
 * `topbar-panels.jsx:4` states the split in one line: *"Megaphone (separate) =
 * official announcements. Calendar = what's coming up."* The bell is activity
 * about you. They are different endpoints with different read semantics, and
 * one glyph for both would be the interface claiming they are the same thing.
 */
export function IconMegaphone(props) {
    return _jsx(Material, { glyph: "megaphone", ...props });
}
export function IconSearch(props) {
    return _jsx(Material, { glyph: "search", ...props });
}
export function IconSparkle(props) {
    return _jsx(Material, { glyph: "sparkle", ...props });
}
/**
 * The brand's four-point compass-star as a glyph — exempt from the Material
 * swap (D7). Same box and same fill as before the swap, so the Journey tab
 * did not move.
 */
export function IconStar4(props) {
    return (_jsx(Icon, { viewBox: "0 0 24 24", ...props, children: _jsx("path", { d: "M12 2l2 8 8 2-8 2-2 8-2-8-8-2 8-2z" }) }));
}
export function IconTornado(props) {
    return _jsx(Material, { glyph: "tornado", ...props });
}
export function IconAnchor(props) {
    return _jsx(Material, { glyph: "anchor", ...props });
}
export function IconChevronRight(props) {
    return _jsx(Material, { glyph: "chevronRight", ...props });
}
export function IconChevronDown(props) {
    return _jsx(Material, { glyph: "chevronDown", ...props });
}
export function IconPlus(props) {
    return _jsx(Material, { glyph: "plus", ...props });
}
export function IconPlay(props) {
    return _jsx(Material, { glyph: "play", ...props });
}
export function IconClock(props) {
    return _jsx(Material, { glyph: "clock", ...props });
}
export function IconHeadphones(props) {
    return _jsx(Material, { glyph: "headphones", ...props });
}
export function IconHeart(props) {
    return _jsx(Material, { glyph: "heart", ...props });
}
export function IconMessage(props) {
    return _jsx(Material, { glyph: "message", ...props });
}
export function IconCalendar(props) {
    return _jsx(Material, { glyph: "calendar", ...props });
}
export function IconSettings(props) {
    return _jsx(Material, { glyph: "settings", ...props });
}
export function IconArrowUpRight(props) {
    return _jsx(Material, { glyph: "arrowUpRight", ...props });
}
export function IconCheck(props) {
    return _jsx(Material, { glyph: "check", ...props });
}
/** The theme control's two faces. */
export function IconMoon(props) {
    return _jsx(Material, { glyph: "moon", ...props });
}
export function IconSun(props) {
    return _jsx(Material, { glyph: "sun", ...props });
}
/** The award rosette on the certifications strip and the `Certified` pill. */
export function IconAward(props) {
    return _jsx(Material, { glyph: "award", ...props });
}
export function IconShare(props) {
    return _jsx(Material, { glyph: "share", ...props });
}
/**
 * The error surface's glyph (D9: navy on Rose doux, WITH an icon). The disc
 * rather than the triangle — a failed submit is not a hazard warning.
 */
export function IconAlert(props) {
    return _jsx(Material, { glyph: "alert", ...props });
}
/**
 * The composer's tool row: add a photo, add a video (a marked V2 shell), attach
 * a file (same), open the emoji strip. Filled Material, so the round buttons
 * read as one set with the rest of the product rather than as emoji.
 */
export function IconAddPhoto(props) {
    return _jsx(Material, { glyph: "addPhoto", ...props });
}
/**
 * A poll — three rounded bars. Not from the Material import (its script is not
 * in this checkout), so drawn on the same 960 box the imported glyphs use.
 */
export function IconPoll(props) {
    return (_jsx(Icon, { viewBox: MATERIAL_VIEWBOX, ...props, children: _jsx("path", { d: "M220-160q-25 0-42.5-17.5T160-220v-260q0-25 17.5-42.5T220-540q25 0 42.5 17.5T280-480v260q0 25-17.5 42.5T220-160Zm260 0q-25 0-42.5-17.5T420-220v-520q0-25 17.5-42.5T480-800q25 0 42.5 17.5T540-740v520q0 25-17.5 42.5T480-160Zm260 0q-25 0-42.5-17.5T680-220v-140q0-25 17.5-42.5T740-420q25 0 42.5 17.5T800-360v140q0 25-17.5 42.5T740-160Z" }) }));
}
export function IconVideo(props) {
    return _jsx(Material, { glyph: "video", ...props });
}
export function IconAttachFile(props) {
    return _jsx(Material, { glyph: "attachFile", ...props });
}
export function IconMood(props) {
    return _jsx(Material, { glyph: "mood", ...props });
}
/**
 * A close and a bin. Close is the header control on a pushed sheet — the only
 * way out that is not a system gesture on a phone; Delete marks the one
 * destructive row in the account surface.
 */
export function IconClose(props) {
    return _jsx(Material, { glyph: "close", ...props });
}
export function IconDelete(props) {
    return _jsx(Material, { glyph: "delete", ...props });
}
/**
 * Mobile branding audit r2 (11 Sep): the five glyphs the app still drew by
 * hand or set as text. A star that is lit by colour, never outlined; the
 * streak leaf; the pencil; the back chevron; and the More tab's own glyph, so
 * it stops sharing the compass with Case Studies.
 */
export function IconStar(props) {
    return _jsx(Material, { glyph: "star", ...props });
}
export function IconLeaf(props) {
    return _jsx(Material, { glyph: "leaf", ...props });
}
export function IconEdit(props) {
    return _jsx(Material, { glyph: "edit", ...props });
}
export function IconChevronLeft(props) {
    return _jsx(Material, { glyph: "chevronLeft", ...props });
}
export function IconMore(props) {
    return _jsx(Material, { glyph: "more", ...props });
}
/** The overflow « ••• » — a per-item menu (report / block). Not the More tab. */
export function IconMoreHoriz(props) {
    return _jsx(Material, { glyph: "moreHoriz", ...props });
}
/** Étape 1 of the TSP journey — « changement de lunette ». */
export function IconEyeglasses(props) {
    return _jsx(Material, { glyph: "eyeglasses", ...props });
}
/** Étape 2 of the TSP journey — « la boîte à outils ». */
export function IconToolbox(props) {
    return _jsx(Material, { glyph: "toolbox", ...props });
}
/**
 * Web branding audit r2 (11 Sep): the glyphs the web screens still drew as
 * their own stroke SVGs. With these, every icon a screen needs is in the set,
 * and `apps/web/src/lib/tests/brand-icons.test.ts` fails on the next
 * hand-drawn one.
 */
/** In-flow forward arrow — "Log in →", "before → now". `IconArrowUpRight` is the outward link. */
export function IconArrowForward(props) {
    return _jsx(Material, { glyph: "arrowForward", ...props });
}
/** The padlock beside "secure payment" and on the password row. */
export function IconLock(props) {
    return _jsx(Material, { glyph: "lock", ...props });
}
/** Pairs with `IconPlay` on the audio player, which used ▶ / ❚❚ as text. */
export function IconPause(props) {
    return _jsx(Material, { glyph: "pause", ...props });
}
/** The profile's "public" eyebrow — Material's globe. */
export function IconGlobe(props) {
    return _jsx(Material, { glyph: "globe", ...props });
}
export function IconDownload(props) {
    return _jsx(Material, { glyph: "download", ...props });
}
/** A document row: the profile's data export, an invoice. */
export function IconDocument(props) {
    return _jsx(Material, { glyph: "document", ...props });
}
export function IconCreditCard(props) {
    return _jsx(Material, { glyph: "creditCard", ...props });
}
//# sourceMappingURL=icons.js.map